import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaArrowLeft, FaBoxOpen, FaTruck, FaFileDownload, FaCalendar, FaBox, FaShippingFast, FaBarcode, FaBoxes } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import './ReturnDetails.css';

// Dummy data for "experience" role - detailed return profiles
const dummyExperienceReturnDetails = {
  'exp_ret_1': {
    _id: 'exp_ret_1',
    status: 'pending',
    created_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    items: [
      { product_id: 'exp001_ret', title: 'Experience T-Shirt - Galaxy', quantity: 1, price: 29.99, image_url: 'https://via.placeholder.com/80x80.png?text=Galaxy+Tee', weight: 200 }
    ],
    customer_email: 'john.exp@example.com',
    order_id: 'EXP1001',
    reason: 'Incorrect size received by customer.',
    summary: 'Customer reports receiving a size M instead of L for the Galaxy T-Shirt. Awaiting customer to ship back the item.',
    tracking_number: null, // No tracking yet for pending
    label_url: null,
    trackingInfo: null // No tracking info if no tracking number
  },
  'exp_ret_2': {
    _id: 'exp_ret_2',
    status: 'approved',
    created_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    items: [
      { product_id: 'exp004_ret', title: 'Rocket Cap', quantity: 1, price: 19.99, image_url: 'https://via.placeholder.com/80x80.png?text=Rocket+Cap', weight: 100 }
    ],
    customer_email: 'alpha.test@example.com',
    order_id: 'EXP1003',
    reason: 'Item arrived damaged (defective stitching). Photo provided.',
    summary: 'Return approved after customer provided evidence of damage. Label sent to customer.',
    tracking_number: 'RETURN_TRACK_EXP002',
    label_url: '#dummy-label-link-2',
    trackingInfo: { // Dummy tracking info
      status: 'In Transit',
      tracking_events: [
        { date: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(), location: 'Origin Facility, Testville', status: 'Package picked up by carrier' },
        { date: new Date(Date.now() - 0.5 * 24 * 60 * 60 * 1000).toISOString(), location: 'Sorting Hub, Midpoint City', status: 'Departed from facility' },
      ]
    }
  },
  'exp_ret_3': {
    _id: 'exp_ret_3',
    status: 'completed',
    created_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    items: [
      { product_id: 'exp006_ret', title: 'Alien Socks', quantity: 2, price: 12.99, image_url: 'https://via.placeholder.com/80x80.png?text=Alien+Socks', weight: 50 }
    ],
    customer_email: 'sample.cust@example.com',
    order_id: 'EXP1004',
    reason: 'Customer changed their mind about the product.',
    summary: 'Return received and processed. Refund issued to customer.',
    tracking_number: 'RETURN_TRACK_EXP003',
    label_url: '#dummy-label-link-3',
    trackingInfo: { // Dummy tracking info
      status: 'Delivered',
      tracking_events: [
        { date: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(), location: 'Customer Drop-off Point', status: 'Package dropped off' },
        { date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(), location: 'Return Center, Warehouse Z', status: 'Arrived at return facility' },
        { date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), location: 'Return Center, Warehouse Z', status: 'Delivered and signed for' },
      ]
    }
  }
};

const ReturnDetails = () => {
  const { returnId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [returnDetails, setReturnDetails] = useState(null);
  const [trackingInfo, setTrackingInfo] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isTrackingLoading, setIsTrackingLoading] = useState(false);

  useEffect(() => {
    const fetchReturnDetails = async () => {
      setIsLoading(true);
      setIsTrackingLoading(false); // Reset tracking loading
      setError(null);
      try {
        const token = sessionStorage.getItem('access_token');
        const userDataString = sessionStorage.getItem('userData');
        let userRole = null;

        if (!token) {
          navigate('/');
          setIsLoading(false);
          return;
        }
        
        if (userDataString) {
          try {
            userRole = JSON.parse(userDataString).role;
          } catch (parseError) {
            console.error("Failed to parse user data from sessionStorage:", parseError);
          }
        }

        if (userRole === 'experience') {
          console.log(`Experience user detected. Looking for dummy return details for ID: ${returnId}`);
          const dummyReturn = dummyExperienceReturnDetails[returnId];
          if (dummyReturn) {
            setReturnDetails(dummyReturn);
            if (dummyReturn.trackingInfo) {
              setTrackingInfo(dummyReturn.trackingInfo);
            } else {
              setTrackingInfo(null); // Ensure tracking info is reset if not present
            }
          } else {
            console.warn(`Dummy return details for ID ${returnId} not found for experience user.`);
            setError(t('returnDetails.notFoundExperience', `Details for experience return ${returnId} not found.`));
            setReturnDetails(null);
          }
          setIsLoading(false);
          // No separate tracking loading for dummy data as it's included
          return; 
        }

        // Real API call for other users
        const response = await axios.get(`/api/webhook/returns/${returnId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setReturnDetails(response.data);
        
        if (response.data && response.data.tracking_number) {
          setIsTrackingLoading(true);
          try {
            const trackingResponse = await axios.get(
              `/api/logistics/tracking/${response.data.tracking_number}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            setTrackingInfo(trackingResponse.data);
          } catch (trackingErr) {
            console.error("Error fetching tracking info:", trackingErr);
            // Set error for tracking separately or just log, main details might still be useful
            setError(prevError => prevError ? `${prevError}. ${t('returnDetails.trackingError')}` : t('returnDetails.trackingError'));
            setTrackingInfo(null); // Clear tracking info on error
          } finally {
            setIsTrackingLoading(false);
          }
        } else {
          setTrackingInfo(null); // No tracking number, so no tracking info
        }
      } catch (err) {
        console.error("Error fetching return details:", err);
        setError(err.message);
        if (err.response && (err.response.status === 401 || err.response.status === 422)) {
          sessionStorage.removeItem('access_token');
          sessionStorage.removeItem('userData');
          navigate('/');
        }
      } finally {
        setIsLoading(false); 
        // isTrackingLoading is handled within its own try/catch/finally for real users
      }
    };

    fetchReturnDetails();
  }, [returnId, navigate, t]); // Added navigate and t to dependencies

  const formatDate = (isoString) => {
    return new Date(isoString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (error) return <div className="una-return-details-error">{t('returnDetails.error')}: {error}</div>;
  if (isLoading) {
    return (
      <div className="una-dashboard-loading-screen">
        <div className="una-loading-content">
          <div className="una-loading-logo">
            <img src="/bgless_logo.png" alt="Unanimity AI Logo" />
          </div>
          <h2>{t('returnDetails.loadingTitle')}</h2>
          <div className="una-loading-progress">
            <div className="una-progress-bar">
              <div className="una-progress-glow"></div>
            </div>
          </div>
          <div className="una-loading-metrics">
            <div className="una-metric-item">
              <FaBoxOpen />
              <span>{t('returnDetails.processingInfo')}</span>
            </div>
            <div className="una-metric-item">
              <FaTruck />
              <span>{t('returnDetails.fetchingShipping')}</span>
            </div>
            <div className="una-metric-item">
              <FaBoxes />
              <span>{t('returnDetails.loadingItems')}</span>
            </div>
            {returnDetails?.tracking_number && (
              <div className="una-metric-item">
                <FaBarcode />
                <span>{t('returnDetails.retrievingTracking')}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
  if (!returnDetails) {
     return (
      <div className="una-return-details-error">
        <FaBoxOpen size={50} style={{marginBottom: '20px'}} />
        <h2>{t('returnDetails.notFoundTitle', 'Return Not Found')}</h2>
        <p>{error || t('returnDetails.notFound')}</p>
        <button className="una-back-button" onClick={() => navigate('/returns')}>
          <FaArrowLeft /> {t('returnDetails.backToReturns')}
        </button>
      </div>
    );
  }

  return (
    <div className="una-return-container">
      <div className="una-return-panels">
        <div className="una-return-left-panel">
          <div className="una-return-nav">
            <button onClick={() => navigate(-1)} className="una-return-back-btn">
              <FaArrowLeft /> {t('returnDetails.backToReturns')}
            </button>
          </div>

          <div className="una-return-header">
            <div className="una-return-title">
              <h1>{t('returnDetails.return')} {returnId.substring(0, 8)}</h1>
              <div className="una-return-meta">
                <span className="una-return-date">
                  <FaCalendar /> {formatDate(returnDetails.created_at)}
                </span>
                <span className={`una-return-status-badge ${returnDetails.status.toLowerCase().replace(' ', '-')}`}>
                  {returnDetails.status}
                </span>
              </div>
            </div>
          </div>

          {returnDetails.summary && (
            <section className="una-return-section una-return-summary">
              <div className="una-return-section-header">
                <h2>{t('returnDetails.summary')}</h2>
              </div>
              <p className="una-return-text">{returnDetails.summary}</p>
            </section>
          )}
        </div>

        <div className="una-return-right-panel">
          <section className="una-return-section una-return-shipping">
            {returnDetails.tracking_number && (
              <>
                <div className="una-return-section-header">
                  <FaTruck />
                  <h2>{t('returnDetails.shippingInfo')}</h2>
                </div>
                <div className="una-return-tracking-info">
                  <div className="una-return-tracking-number">
                    <span className="tracking-label">{t('returnDetails.trackingNumber')}</span>
                    <span className="tracking-value una-return-text">{returnDetails.tracking_number}</span>
                  </div>
                  {returnDetails.label_url && (
                    <a 
                      href={returnDetails.label_url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="una-return-label-btn"
                    >
                      <FaFileDownload /> {t('returnDetails.downloadLabel')}
                    </a>
                  )}
                  
                  {isTrackingLoading ? (
                    <div className="una-tracking-loading">{t('returnDetails.loadingTracking')}</div>
                  ) : trackingInfo && (
                    <div className="una-tracking-details">
                      <div className="una-tracking-status">
                        <span className="tracking-status-label">{t('returnDetails.currentStatus')}:</span>
                        <span className={`tracking-status-value ${trackingInfo.status.toLowerCase().replace(' ', '-')}`}>
                          {trackingInfo.status}
                        </span>
                      </div>
                      <div className="una-tracking-timeline">
                        {trackingInfo.tracking_events.map((event, index) => (
                          <div key={index} className="una-tracking-event">
                            <div className="event-date">{formatDate(event.date)}</div>
                            <div className="event-location">{event.location}</div>
                            <div className="event-status">{event.status}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </section>

          <section className="una-return-section una-return-items">
            <div className="una-return-section-header">
              <FaBox />
              <h2>{t('returnDetails.itemsReturned')}</h2>
            </div>
            <div className="una-return-items-grid">
              {returnDetails.items.map((item, index) => (
                <div key={index} className="una-return-item-card">
                  <h3 className="una-return-text">{item.title}</h3>
                  <div className="una-return-item-stats">
                    <div className="una-return-item-stat">
                      <span className="stat-label">{t('returnDetails.quantity')}</span>
                      <span className="stat-value una-return-text">{item.quantity}</span>
                    </div>
                    <div className="una-return-item-stat">
                      <span className="stat-label">{t('returnDetails.price')}</span>
                      <span className="stat-value una-return-text">€{item.price}</span>
                    </div>
                    {item.weight && (
                      <div className="una-return-item-stat">
                        <span className="stat-label">{t('returnDetails.weight')}</span>
                        <span className="stat-value una-return-text">{item.weight}g</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ReturnDetails;